import React from 'react';
import { useLocale } from '../contexts/LocaleContext';

const CheckCircle: React.FC<{ light?: boolean }> = ({ light }) => (
  <span
    className={`inline-flex items-center justify-center mr-3 flex-shrink-0 w-6 h-6 rounded-full ${
      light ? 'bg-white/20 text-white' : 'bg-white border border-gray-200 text-green-500'
    }`}
    aria-hidden
  >
    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 6L8.5 13.5L5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const PricingSection: React.FC = () => {
  const { t } = useLocale();
  const plans = (t?.pricing?.plans || []) as Array<any>;

  return (
    <section id="pricing" className="pb-20 pt-10 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="heading-font text-4xl md:text-5xl font-bold text-primary-dark">{t?.pricing?.title || 'Plans'}</h2>
          {t?.pricing?.subtitle && (
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">{t.pricing.subtitle}</p>
          )}
        </div>

        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-3 items-stretch">
          {plans.map((plan: any, idx: number) => {
            const isFeatured = idx === 1;
            return (
              <article
                key={plan.key || idx}
                className={`h-full flex flex-col justify-between rounded-2xl p-6 md:p-8 transition-shadow duration-300 border bg-white ${
                  isFeatured ? 'bg-gradient-primary text-white border-transparent shadow-2xl transform md:scale-105 z-10' : 'shadow-lg border-gray-200'
                }`}
              >
                <div>
                  <div className="mb-4 text-center">
                    <h3 className={`heading-font text-2xl font-semibold ${isFeatured ? 'text-white' : 'text-primary-dark'} text-center`}>{plan.name}</h3>
                    {plan.tagline && <p className={`${isFeatured ? 'text-white/90' : 'text-sm text-gray-500'} mt-1 mx-auto`}>{plan.tagline}</p>}
                  </div>
                  {isFeatured && (
                    <div className="absolute left-1/2 -top-3 transform -translate-x-1/2 z-20">
                      <div className="text-xs bg-black/80 text-white px-4 py-1 rounded-full font-semibold">
                        {t?.pricing?.featuredLabel || 'Most popular'}
                      </div>
                    </div>
                  )}

                  <h4 className={`${isFeatured ? 'text-white/90' : 'text-sm text-gray-500'} uppercase tracking-wider mb-3 text-center`}>{t?.pricing?.featuresTitle || 'FEATURES'}</h4>

                  <div className="space-y-3 mb-6">
                    {(plan.bullets || []).map((b: string, i: number) => (
                      <div key={i} className="flex items-start">
                        <CheckCircle light={isFeatured} />
                        <p className={`${isFeatured ? 'text-white/95' : 'text-gray-700'} leading-relaxed`}>{b}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <a
                    href="#contact"
                    className={`inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold ${isFeatured ? 'bg-white text-primary-dark' : 'bg-gradient-primary text-white'}`}
                  >
                    {t?.pricing?.cta || 'Request demo'}
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
